import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  Alert,
  Linking,
  Modal,
  Platform,
  Pressable,
  SafeAreaView,
  ScrollView,
  Share,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { createAudioPlayer, type AudioPlayer, type AudioStatus } from "expo-audio";
import { Directory, File, Paths } from "expo-file-system";
import type { CallRecording, CallRecordingsResponse } from "@nativecall/shared";

import { fetchCallRecordings } from "../lib/api";

type Props = {
  visible: boolean;
  userId: string;
  callSessionId: string | null;
  destinationNumber?: string | null;
  onClose: () => void;
};

type LoadState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "ready"; data: CallRecordingsResponse }
  | { kind: "error"; message: string };

type FileState =
  | { kind: "pending" }
  | { kind: "downloading" }
  | { kind: "ready"; localUri: string }
  | { kind: "error"; message: string };

type PlayStatus = { streamId: string; playing: boolean } | null;

function recordingsDirForSession(sessionId: string) {
  return new Directory(Paths.cache, "recordings", sessionId);
}

function localFileFor(sessionId: string, streamId: string) {
  return new File(Paths.cache, "recordings", sessionId, `${streamId}.wav`);
}

async function ensureLocalCopy(sessionId: string, rec: CallRecording): Promise<string> {
  const cached = localFileFor(sessionId, rec.streamId);
  if (cached.exists) return cached.uri;
  const dir = recordingsDirForSession(sessionId);
  if (!dir.exists) dir.create({ intermediates: true, idempotent: true });
  const downloaded = await File.downloadFileAsync(rec.url, cached);
  return downloaded.uri;
}

export function CallRecordingsModal({
  visible,
  userId,
  callSessionId,
  destinationNumber,
  onClose,
}: Props) {
  const [state, setState] = useState<LoadState>({ kind: "idle" });
  const [files, setFiles] = useState<Record<string, FileState>>({});
  const [playStatus, setPlayStatus] = useState<PlayStatus>(null);
  const playerRef = useRef<AudioPlayer | null>(null);
  const subscriptionRef = useRef<{ remove: () => void } | null>(null);

  const releasePlayer = useCallback(() => {
    subscriptionRef.current?.remove();
    subscriptionRef.current = null;
    const current = playerRef.current as
      | (AudioPlayer & { release?: () => void; remove?: () => void })
      | null;
    if (current) {
      try {
        current.pause();
      } catch {
        // ignore
      }
      try {
        current.release?.();
      } catch {
        // ignore
      }
      try {
        current.remove?.();
      } catch {
        // ignore
      }
    }
    playerRef.current = null;
    setPlayStatus(null);
  }, []);

  useEffect(() => {
    if (!visible || !callSessionId) return;
    let cancelled = false;
    setState({ kind: "loading" });
    setFiles({});
    fetchCallRecordings(userId, callSessionId)
      .then((data) => {
        if (cancelled) return;
        setState({ kind: "ready", data });
        const initial: Record<string, FileState> = {};
        for (const rec of data.recordings) {
          const cached = localFileFor(callSessionId, rec.streamId);
          initial[rec.streamId] = cached.exists
            ? { kind: "ready", localUri: cached.uri }
            : { kind: "pending" };
        }
        setFiles(initial);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const message =
          error instanceof Error ? error.message : "Unable to load recordings.";
        setState({ kind: "error", message });
      });
    return () => {
      cancelled = true;
    };
  }, [visible, userId, callSessionId]);

  useEffect(() => {
    if (visible) return;
    releasePlayer();
    setState({ kind: "idle" });
    setFiles({});
  }, [visible, releasePlayer]);

  useEffect(() => () => releasePlayer(), [releasePlayer]);

  const setFileState = useCallback((streamId: string, next: FileState) => {
    setFiles((prev) => ({ ...prev, [streamId]: next }));
  }, []);

  const ensureReady = useCallback(
    async (rec: CallRecording): Promise<string | null> => {
      if (!callSessionId) return null;
      const current = files[rec.streamId];
      if (current?.kind === "ready") return current.localUri;
      if (current?.kind === "downloading") return null;
      setFileState(rec.streamId, { kind: "downloading" });
      try {
        const uri = await ensureLocalCopy(callSessionId, rec);
        setFileState(rec.streamId, { kind: "ready", localUri: uri });
        return uri;
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Download failed.";
        setFileState(rec.streamId, { kind: "error", message });
        return null;
      }
    },
    [callSessionId, files, setFileState],
  );

  const handleTogglePlay = useCallback(
    async (rec: CallRecording) => {
      if (playStatus?.streamId === rec.streamId && playStatus.playing) {
        playerRef.current?.pause();
        setPlayStatus({ streamId: rec.streamId, playing: false });
        return;
      }
      if (playStatus?.streamId === rec.streamId && playerRef.current) {
        try {
          playerRef.current.play();
          setPlayStatus({ streamId: rec.streamId, playing: true });
          return;
        } catch {
          releasePlayer();
        }
      }

      const localUri = await ensureReady(rec);
      if (!localUri) return;

      releasePlayer();
      try {
        const next = createAudioPlayer({ uri: localUri });
        playerRef.current = next;
        const sub = next.addListener("playbackStatusUpdate", (status: AudioStatus) => {
          setPlayStatus((prev) => {
            const streamId = rec.streamId;
            if (prev && prev.streamId !== streamId) return prev;
            if (status.didJustFinish) {
              return { streamId, playing: false };
            }
            return { streamId, playing: status.playing };
          });
        });
        subscriptionRef.current = sub;
        next.play();
        setPlayStatus({ streamId: rec.streamId, playing: true });
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Playback failed.";
        Alert.alert("Couldn't play", message);
      }
    },
    [ensureReady, playStatus, releasePlayer],
  );

  const handleShare = useCallback(
    async (rec: CallRecording) => {
      const localUri = await ensureReady(rec);
      if (!localUri) {
        const st = files[rec.streamId];
        const msg =
          st?.kind === "error"
            ? st.message
            : "Still downloading — try again in a moment.";
        Alert.alert("Can't share yet", msg);
        return;
      }
      const fileName = `${rec.label.replace(/[^\w]+/g, "-")}-${rec.streamId}.wav`;
      try {
        if (Platform.OS === "ios") {
          await Share.share({
            url: localUri,
            title: fileName,
          });
          return;
        }
        await Share.share({
          title: fileName,
          message: `${rec.label} (download the file from the signed link):\n${rec.url}`,
          url: rec.url,
        });
      } catch (error) {
        if (error instanceof Error && /dismiss/i.test(error.message)) return;
        Linking.openURL(rec.url).catch(() => undefined);
      }
    },
    [ensureReady, files],
  );

  const sortedRecordings = useMemo(() => {
    if (state.kind !== "ready") return [];
    const order = [
      "caller-perspective",
      "callee-perspective",
      "caller-original",
      "callee-original",
      "translation-to-caller",
      "translation-to-callee",
    ];
    return [...state.data.recordings].sort(
      (a, b) => order.indexOf(a.streamId) - order.indexOf(b.streamId),
    );
  }, [state]);

  return (
    <Modal
      animationType="slide"
      presentationStyle="pageSheet"
      visible={visible}
      onRequestClose={onClose}
    >
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.header}>
          <Pressable onPress={onClose} hitSlop={12}>
            <Text style={styles.closeText}>Close</Text>
          </Pressable>
          <Text style={styles.headerTitle} numberOfLines={1}>
            Recording
          </Text>
          <View style={styles.headerSpacer} />
        </View>
        {destinationNumber ? (
          <Text style={styles.subheader} numberOfLines={1}>
            {destinationNumber}
          </Text>
        ) : null}

        <ScrollView contentContainerStyle={styles.scroll}>
          {state.kind === "loading" ? (
            <ActivityIndicator color="#16302b" />
          ) : null}
          {state.kind === "error" ? (
            <Text style={styles.errorText}>{state.message}</Text>
          ) : null}
          {state.kind === "ready" ? (
            <>
              {sortedRecordings.map((rec) => {
                const fs = files[rec.streamId] ?? { kind: "pending" };
                const isActive =
                  playStatus?.streamId === rec.streamId && playStatus.playing;
                const playDisabled = fs.kind === "downloading";
                const playLabel =
                  fs.kind === "downloading" && !isActive
                    ? "Loading…"
                    : isActive
                      ? "Pause"
                      : "Play";
                return (
                  <View key={rec.streamId} style={styles.card}>
                    <Text style={styles.cardTitle}>{rec.label}</Text>
                    <Text style={styles.cardMeta}>
                      {formatBytes(rec.sizeBytes)} •{" "}
                      {rec.channels === 2 ? "Stereo" : "Mono"} • μ-law WAV
                    </Text>
                    {fs.kind === "error" ? (
                      <Text style={styles.errorMeta}>{fs.message}</Text>
                    ) : null}
                    <View style={styles.buttonRow}>
                      <Pressable
                        style={[
                          styles.actionButton,
                          styles.playButton,
                          playDisabled ? styles.buttonDisabled : null,
                        ]}
                        disabled={playDisabled}
                        onPress={() => {
                          handleTogglePlay(rec).catch(() => undefined);
                        }}
                      >
                        <Text style={styles.actionButtonText}>{playLabel}</Text>
                      </Pressable>
                      <Pressable
                        style={[styles.actionButton, styles.shareButton]}
                        onPress={() => {
                          handleShare(rec).catch(() => undefined);
                        }}
                      >
                        <Text style={styles.actionButtonText}>
                          {fs.kind === "downloading" ? "Saving…" : "Share / Save"}
                        </Text>
                      </Pressable>
                    </View>
                  </View>
                );
              })}
              <Text style={styles.footer}>
                Files download on demand, then play + share directly from the
                device. Recordings auto-delete after 7 days.
              </Text>
            </>
          ) : null}
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );
}

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: "#f4efe7",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  closeText: {
    color: "#16302b",
    fontSize: 16,
    fontWeight: "600",
  },
  headerTitle: {
    color: "#16302b",
    fontSize: 17,
    fontWeight: "700",
  },
  headerSpacer: {
    width: 48,
  },
  subheader: {
    color: "#3b4a44",
    fontSize: 14,
    paddingHorizontal: 16,
    paddingBottom: 12,
  },
  scroll: {
    padding: 16,
    gap: 12,
  },
  card: {
    backgroundColor: "#ffffff",
    borderRadius: 12,
    padding: 16,
    gap: 6,
  },
  cardTitle: {
    color: "#16302b",
    fontSize: 16,
    fontWeight: "600",
  },
  cardMeta: {
    color: "#64786f",
    fontSize: 13,
  },
  errorMeta: {
    color: "#c0392b",
    fontSize: 12,
  },
  buttonRow: {
    flexDirection: "row",
    gap: 8,
    marginTop: 8,
  },
  actionButton: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 8,
    alignItems: "center",
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  playButton: {
    backgroundColor: "#16302b",
  },
  shareButton: {
    backgroundColor: "#3558a0",
  },
  actionButtonText: {
    color: "#ffffff",
    fontWeight: "600",
  },
  errorText: {
    color: "#c0392b",
    fontSize: 15,
  },
  footer: {
    color: "#64786f",
    fontSize: 12,
    marginTop: 16,
    textAlign: "center",
  },
});
